import React, { useState } from 'react';
import { ShieldCheck, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../services/api';

function FirstLoginPasswordModal({ user, onPasswordChanged }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const rules = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password)
  };
  const isPasswordValid = Object.values(rules).every(Boolean);
  const doPasswordsMatch = password && confirmPassword && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!isPasswordValid) {
      setError('Please fulfill all password security requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please re-enter.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const res = await api.updateMyProfile({ password });
      const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
      savedUser.mustChangePassword = false;
      localStorage.setItem('user', JSON.stringify(savedUser));
      if (api.user) api.user.mustChangePassword = false;
      onPasswordChanged(res);
    } catch (err) {
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(8px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        animation: 'fadeIn 0.25s ease-out'
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '480px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{ padding: '2rem 2rem 1.25rem 2rem', textAlign: 'center', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <div
            style={{
              width: '54px',
              height: '54px',
              borderRadius: '16px',
              background: 'rgba(var(--primary-rgb), 0.12)',
              color: 'var(--primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem auto'
            }}
          >
            <ShieldCheck size={28} />
          </div>

          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-main)' }}>
            Set Your Permanent Password
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
            Welcome to Devorants! Your account was initialized with a temporary password. Please set your personal password to activate and secure your account.
          </p>
        </div>

        {/* Body Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.75rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {error && (
            <div
              style={{
                padding: '0.75rem 1rem',
                borderRadius: 'var(--radius-sm)',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: 'var(--danger)',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          {/* New Password */}
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
              New Permanent Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new strong password"
                required
                disabled={loading}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  height: '44px',
                  padding: '0 2.75rem 0 1rem',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  color: 'var(--text-main)',
                  outline: 'none',
                  fontSize: '0.92rem'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Checklist */}
            <div style={{ marginTop: '0.65rem', padding: '0.75rem 0.9rem', background: 'var(--surface-hover)', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              <div style={{ fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.1rem' }}>Password Requirements:</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem' }}>
                <span style={{ color: rules.length ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  {rules.length ? <CheckCircle2 size={13} /> : '○'} 8+ characters
                </span>
                <span style={{ color: rules.upper ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  {rules.upper ? <CheckCircle2 size={13} /> : '○'} Uppercase (A-Z)
                </span>
                <span style={{ color: rules.lower ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  {rules.lower ? <CheckCircle2 size={13} /> : '○'} Lowercase (a-z)
                </span>
                <span style={{ color: rules.number && rules.special ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  {rules.number && rules.special ? <CheckCircle2 size={13} /> : '○'} Number & Symbol
                </span>
              </div>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
              Confirm Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'}
                placeholder="Re-enter your new password"
                required
                disabled={loading}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{
                  width: '100%',
                  height: '44px',
                  padding: '0 2.75rem 0 1rem',
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${confirmPassword && !doPasswordsMatch ? 'var(--danger)' : 'var(--border)'}`,
                  background: 'var(--surface)',
                  color: 'var(--text-main)',
                  outline: 'none',
                  fontSize: '0.92rem'
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {confirmPassword && !doPasswordsMatch && (
              <span style={{ fontSize: '0.75rem', color: 'var(--danger)', marginTop: '0.25rem', display: 'block' }}>
                Passwords do not match
              </span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !isPasswordValid || !doPasswordsMatch}
            style={{
              height: '44px',
              marginTop: '0.5rem',
              borderRadius: 'var(--radius-sm)',
              background: (loading || !isPasswordValid || !doPasswordsMatch) ? 'var(--border)' : 'var(--primary)',
              color: (loading || !isPasswordValid || !doPasswordsMatch) ? 'var(--text-muted)' : '#fff',
              border: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: (loading || !isPasswordValid || !doPasswordsMatch) ? 'not-allowed' : 'pointer',
              boxShadow: (loading || !isPasswordValid || !doPasswordsMatch) ? 'none' : '0 4px 14px rgba(var(--primary-rgb), 0.3)',
              transition: 'all 0.2s'
            }}
          >
            {loading ? 'Saving New Password...' : 'Save Password & Enter Dashboard'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
            <button
              type="button"
              onClick={() => { api.logout(); window.location.href = '/login'; }}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Sign out instead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FirstLoginPasswordModal;

